import SlideIn from 'app/components/SlideIn/SlideIn';
import {
  CancelIcon,
  CutleryIconWonWithBG,
  EditPencilIcon,
  GearIcon,
  MoneyIcon,
  PlusIcon,
  SearchIcon,
  SwitchIcon,
} from 'app/components/SvgAssets';
import {
  getDropdownIconClassNames,
  getRequestStatusClassNames,
  getTabClassNames,
} from 'app/dispatcher/modules/components/ClassUtils';
import {
  useGetServiceProducts,
  useGetServiceProfile,
  useUpdateServiceProfile,
} from 'app/hooks/requests/commerce';
import { useGetServiceProfiles } from 'app/hooks/services';
import { useGetProfile } from 'app/hooks/user';
import {
  ArchiveDrawerWithBG,
  ProfileIconWithBG,
} from 'app/operator/components/SvgAssets';
import { currencyFormatter, getFirstItem } from 'app/utilities/helpers';
import { COMMERCE } from 'app/utilities/roles';
import { privateRoutes } from 'app/utilities/routes';
import {
  GetServiceProducts,
  Request,
  RequestTypes,
  ServiceOutletType,
  ServiceProfile,
} from 'app/utilities/types/shared';
import Dropdown from 'assets/images/dropdown.svg';
import { useFormik } from 'formik';
import { truncate } from 'lodash';
import { DateTime } from 'luxon';
import { Fragment, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ElrInput,
  ElrLocationSelect,
  ElrModal,
  ElrPhoneInputFlat,
} from 'ui-components';
import {
  ElrButton,
  ElrButtonPreIcon,
} from 'ui-components/components/ElrButton';
import { ElrRadio } from 'ui-components/components/ElrRadio';
import TimePicker from 'ui-components/components/ErlTimePicker';
import * as Yup from 'yup';
import { DashboardCards } from '../components/DashboardCards';
import Settings from '../components/Settings';
import { SalesTurnOffModal } from '../marketRuns/MarketModals';
import { MoreDropdown } from '../MoreDropdown';
import UploadDashboard from '../UploadDashboard';

/* eslint-disable-next-line no-shadow */
enum TabNames {
  NEW_ORDERS = 'New Orders',
  ONGOING = 'Ongoing',
  COMPLETED = 'Completed',
}

const validationSchema = Yup.object({
  servicePhone: Yup.string().required('Phone number is required'),
  fullAddress: Yup.object({
    latitude: Yup.number().required('Latitude is required'),
    longitude: Yup.number().required('Longitude is required'),
    label: Yup.string().required('Location is required'),
  }).required('Location is required'),
  serviceEmail: Yup.string()
    .email('Invalid email')
    .required('Email is required'),
  openingTime: Yup.string().required('Service opening time is required'),
  closingTime: Yup.string().required('Service closing time is required'),
  serviceName: Yup.string().optional(),
});

const RestaurantDashboard = () => {
  const {
    id: userId,
    accountInformation: { id: roleId },
  } = useGetProfile();
  const { fetchServiceProfiles, data: serviceProfiles } =
    useGetServiceProfiles();

  useEffect(() => {
    if (userId && roleId) {
      fetchServiceProfiles({ userId, roleId, outletType: 'restaurant' });
    }
  }, [fetchServiceProfiles, userId, roleId]);

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState(TabNames.NEW_ORDERS);
  const params = useParams();
  const { isLoading, serviceProfile } = useGetServiceProfile(
    ServiceOutletType.restaurant,
    params.outletTypeId as string
  );
  const [underlinePosition, setUnderlinePosition] = useState({
    left: 0,
    width: 0,
  });
  const [isProductsLoading, setIsProductsLoading] = useState(true);

  const [servicePhone, setServicePhone] = useState<{
    isValid: boolean;
    mobile: string;
    formattedNumber: string;
  }>({
    isValid: false,
    mobile: serviceProfile?.phone || '',
    formattedNumber: serviceProfile?.phone || '',
  });
  const newOrdersTabRef = useRef<HTMLButtonElement | null>(null);
  const ongoingTabRef = useRef<HTMLButtonElement | null>(null);
  const completedTabRef = useRef<HTMLButtonElement | null>(null);
  const [isExpanded, setIsExpanded] = useState<number | null>(null);
  const [isSalesModalOpen, setIsSalesModalOpen] = useState<boolean>(false);
  const deactivateSales = false;
  const { handleUpdateServiceProfile, isLoading: isUpdateLoading } =
    useUpdateServiceProfile();

  const tabs = [
    { name: TabNames.NEW_ORDERS, ref: newOrdersTabRef },
    { name: TabNames.ONGOING, ref: ongoingTabRef },
    { name: TabNames.COMPLETED, ref: completedTabRef },
  ];

  const toggleRow = (index: number) => {
    setIsExpanded(isExpanded === index ? null : index);
  };

  useEffect(() => {
    const activeTabRef = tabs.find((tab) => tab.name === activeTab)?.ref
      .current;
    if (activeTabRef) {
      const activeTabWidth = activeTabRef.offsetWidth;
      setUnderlinePosition({
        left: activeTabRef.offsetLeft + (activeTabWidth - 110) / 2,
        width: 110,
      });
    }
  }, [activeTab]);

  const { fetchServiceProducts, serviceProducts = [] } =
    useGetServiceProducts();
  const [isSettingsOpen, setIsSettingsOpen] = useState<boolean>(false);
  const [isSwitchRestaurantOpen, setIsSwitchRestaurantOpen] =
    useState<boolean>(false);
  const filteredServiceProducts: GetServiceProducts[] = serviceProducts;
  const filteredDocuments: any[] = [];

  useEffect(() => {
    if (params.outletTypeId) {
      fetchServiceProducts({
        outletType: ServiceOutletType.restaurant,
        outletTypeId: params.outletTypeId as string,
      }).finally(() => setIsProductsLoading(false));
    }
  }, [fetchServiceProducts, params.outletTypeId]);

  const { handleSubmit, setFieldValue, values, isValid } = useFormik({
    enableReinitialize: true,
    initialValues: {
      serviceName: serviceProfile?.name || '',
      servicePhone: serviceProfile?.phone || '',
      serviceEmail: serviceProfile?.email || '',
      fullAddress: {
        label: serviceProfile?.address || '',
        latitude: 0,
        longitude: 0,
      },
      openingTime: serviceProfile?.openingTime || '',
      closingTime: serviceProfile?.closingTime || '',
    },
    validationSchema,
    onSubmit: async () => {
      try {
        // Format opening and closing times to ISO format using Luxon
        const formatTimeToISO = (timeValue: any) => {
          if (!timeValue) return undefined;

          // If it's a DateTime object
          if (timeValue && typeof timeValue.toISO === 'function') {
            return timeValue.toISO();
          }

          // If it's a string like "7:00 PM"
          if (typeof timeValue === 'string') {
            const dateTime = DateTime.fromFormat(timeValue, 'h:mm a');
            if (dateTime.isValid) {
              return dateTime.toISO();
            }
          }

          return undefined;
        };

        const payload = {
          name: values.serviceName,
          phone: values.servicePhone,
          email: values.serviceEmail,
          outletType: ServiceOutletType.restaurant,
          outletTypeId: params.outletTypeId as string,
          address: {
            fullAddress: values.fullAddress.label,
            latitude: values.fullAddress.latitude.toString(),
            longitude: values.fullAddress.longitude.toString(),
          },
          opens: formatTimeToISO(values.openingTime),
          closes: formatTimeToISO(values.closingTime),
        };
        await handleUpdateServiceProfile(payload);
      } catch (error) {
        console.error(error);
      }
    },
  });

  const onPhoneInputChange = (payload: {
    isValid: boolean;
    mobile: string;
    formattedNumber: string;
  }) => {
    setServicePhone({
      isValid: payload.isValid,
      mobile: payload.mobile,
      formattedNumber: payload.formattedNumber,
    });
    setFieldValue('servicePhone', payload.formattedNumber);
  };

  const cardData = [
    {
      title: 'Total Sales',
      count: currencyFormatter(0),
      Icon: <MoneyIcon />,
    },
    {
      title: 'Total Added Products',
      count: 0,
      Icon: <ArchiveDrawerWithBG />,
    },
    {
      title: 'Total Orders',
      count: 0,
      Icon: <ProfileIconWithBG />,
    },
  ];

  const [orderStatuses, setOrderStatuses] = useState<Record<string, boolean>>(
    {}
  );
  const handleOrderReady = (trackingId: string) => {
    setOrderStatuses((prev) => ({
      ...prev,
      [trackingId]: true,
    }));
  };

  const isDisabled =
    !isValid || !values.servicePhone || !values.serviceEmail || isLoading;

  const renderEditInfo = (
    <SlideIn
      close={() => setIsModalOpen(false)}
      shown={isModalOpen}
      leftMarginOffset="md:ml-[60%]"
    >
      <p className="text-center pb-6 text-xl">Edit service Information</p>
      <form onSubmit={handleSubmit}>
        <div className="w-full py-8 mx-auto mb-10 bg-elr-white-400">
          <div className="flex flex-col gap-6">
            <div className="w-full">
              <p className="text-base pb-2 text-elr-black-400">Service Name</p>
              <ElrInput
                name="serviceName"
                value={values.serviceName}
                onChange={(e) => setFieldValue('serviceName', e.target.value)}
                className="border-none rounded-none bg-elr-gray !h-12 outline-none"
                placeholder="Service Name"
              />
            </div>

            <div className="w-full">
              <p className="text-base pb-2 text-elr-black-400">
                Service Address
              </p>
              <div className="h-12">
                <ElrLocationSelect
                  className="pr-2"
                  controlHeight={44}
                  currentValue={values.fullAddress?.label}
                  placeholder="Address"
                  onChange={(value) => setFieldValue('fullAddress', value)}
                />
              </div>
            </div>

            <div className="w-full">
              <p className="text-base pb-2 text-elr-black-400">Phone Number</p>
              <ElrPhoneInputFlat
                containerClass="w-full flex !mb-0"
                className="border-none bg-elr-gray h-12"
                value={servicePhone.mobile}
                onChange={onPhoneInputChange}
                placeholder="Phone Number"
              />
            </div>
            {!servicePhone.isValid && servicePhone.mobile && (
              <span className="text-xs text-elr-error">
                Invalid phone number
              </span>
            )}
            <div className="w-full">
              <p className="text-base pb-2 text-elr-black-400">Email Address</p>
              <ElrInput
                name="serviceEmail"
                value={values.serviceEmail}
                onChange={(e) => setFieldValue('serviceEmail', e.target.value)}
                className="border-none rounded-none bg-elr-gray !h-12 outline-none"
                placeholder="Email Address"
              />
            </div>

            <div className="w-full">
              <p className="text-base pb-2 text-elr-black-400">Opening Time</p>
              <div className="flex items-center justify-between w-full h-12 bg-elr-gray">
                <TimePicker
                  className="w-full bg-elr-gray pr-4"
                  placeholder="Opening Time"
                  clearIcon={null}
                  value={values.openingTime}
                  onChange={(value) => setFieldValue('openingTime', value)}
                />
              </div>
            </div>

            <div className="w-full">
              <p className="text-base pb-2 text-elr-black-400">Closing Time</p>
              <div className="flex items-center justify-between w-full h-12 bg-elr-gray">
                <TimePicker
                  className="w-full bg-elr-gray pr-4"
                  placeholder="Closing Time"
                  clearIcon={null}
                  value={values.closingTime}
                  onChange={(value) => setFieldValue('closingTime', value)}
                />
              </div>
            </div>
          </div>
        </div>

        <ElrButton
          text="Save Changes"
          className="w-full bg-elr-black text-white rounded-md text-base mt-24"
          submit
          loading={isUpdateLoading}
          disabled={isDisabled || isUpdateLoading}
        />
      </form>
    </SlideIn>
  );

  const _renderLoadState = () => {
    if (isLoading) {
      return (
        <tr>
          <td
            colSpan={4}
            className="text-center italic py-8 text-elr-black-300"
          >
            Loading...
          </td>
        </tr>
      );
    }

    const emptyMessages: Record<string, React.ReactNode> = {
      [TabNames.NEW_ORDERS]: <>There are no new orders.</>,
      [TabNames.ONGOING]: <>No ongoing orders at the moment.</>,
      [TabNames.COMPLETED]: <>No completed orders available.</>,
    };

    return (
      <tr>
        <td colSpan={4} className="text-center italic py-8 text-elr-black-300">
          {emptyMessages[activeTab] || 'No data available.'}
        </td>
      </tr>
    );
  };

  /** TODO: Add a loading state here when fetching the service profile */
  if (isLoading || isProductsLoading) {
    return <div className="h-screen p-5">Loading...</div>;
  }

  const isUploadDashboard = () => {
    if (!filteredServiceProducts.length) {
      return <UploadDashboard outletType={ServiceOutletType.restaurant} />;
    }

    return (
      <div className="overflow-x-auto w-full relative">
        <table className="table-auto w-full">
          <thead>
            <tr className="border-b-2 border-elr-gray text-elr-black">
              <th className="font-normal py-7 text-left md:pl-20 pl-6 flex-grow ">
                Order ID
              </th>
              <th className="font-normal py-7 text-center md:w-1/3 flex-grow truncate">
                Order Details
              </th>
              <th className="font-normal py-7 text-center flex-grow">Amount</th>
              {activeTab === TabNames.COMPLETED && (
                <th className="font-normal py-7 text-center flex-grow">
                  Date & Time
                </th>
              )}
              <th className="font-normal py-7 text-right flex-grow " />
            </tr>
          </thead>
          <tbody>
            {filteredDocuments.length
              ? filteredDocuments.map((request: Request, index: number) => (
                  <Fragment key={request.trackingId}>
                    <TableRow
                      request={request}
                      activeTab={activeTab}
                      index={index}
                      toggleRow={toggleRow}
                      isExpanded={isExpanded}
                      isOrderReady={orderStatuses[request.trackingId] || false}
                    />
                    {isExpanded === index && (
                      <ExpandedRow
                        request={request}
                        activeTab={activeTab}
                        isOrderReady={
                          orderStatuses[request.trackingId] || false
                        }
                        onOrderReady={() =>
                          handleOrderReady(request.trackingId)
                        }
                      />
                    )}
                  </Fragment>
                ))
              : _renderLoadState()}
          </tbody>
        </table>
      </div>
    );
  };

  const moreOption = [
    {
      value: 'edit-business-profile',
      label: 'Edit Restaurant Profile',
      icon: <EditPencilIcon />,
      labelClassName: 'text-elr-green',
    },
    ...(deactivateSales
      ? [
          {
            value: 'activate-sales',
            label: 'Activate Sales',
            icon: <SwitchIcon />,
            labelClassName: 'text-elr-green',
          },
        ]
      : [
          {
            value: 'deactivate-sales',
            label: 'Deactivate Sales',
            icon: <CancelIcon width="16" height="16" />,
            labelClassName: 'text-elr-error',
          },
        ]),
  ];

  const handleSelectMoreOption = (selectedValue: string) => {
    if (selectedValue === 'edit-business-profile') {
      setIsModalOpen(true);
    }
    if (selectedValue === 'deactivate-sales') {
      setIsSalesModalOpen(true);
    }
  };

  return (
    <div className="bg-elr-gray flex flex-col pt-8 px-7 md:pt-3 md:px-20 min-h-screen">
      <section className="md:flex md:flex-row flex-col items-center pt-6 justify-between pb-8">
        <div className=" text-elr-black font-normal font-serif leading-tight">
          <h3 className="text-xl mb-2">E-commerce</h3>
          <p className="text-elr-black-300 text-base">
            View and follow up your products and sales
          </p>
        </div>
        <div className="flex flex-wrap gap-4 items-center md:pt-0 pt-8">
          {isModalOpen && renderEditInfo}
          {isSalesModalOpen && (
            <SalesTurnOffModal
              isOpen={isSalesModalOpen}
              onClose={() => setIsSalesModalOpen(false)}
              onClick={() => {}}
              loading={false}
            />
          )}
          <Link
            to={`/${COMMERCE}/${privateRoutes.ecommerce}/${privateRoutes.restaurant}/${params.outletTypeId}/${privateRoutes.addedRestaurantProducts}`}
          >
            <ElrButton
              text={'View Products'}
              onClick={() => {}}
              className="bg-elr-white-400 text-elr-black rounded-md py-3 px-5"
            />
          </Link>
          {/** TODO: At a later time, use the service profile id instead of the user id */}
          <Link
            to={`/${COMMERCE}/${privateRoutes.ecommerce}/${privateRoutes.restaurant}/${privateRoutes.restaurantList}`}
          >
            <ElrButtonPreIcon
              text={'Add New Restaurant'}
              icon={<PlusIcon fill="#081120" />}
              className="bg-white text-elr-black rounded-md py-3 px-5"
            />
          </Link>
          <MoreDropdown
            options={moreOption}
            onSelect={(selectedValue) => handleSelectMoreOption(selectedValue)}
            dropdownPosition="right"
            dropdownOptionClassName="hover:bg-elr-gray-100"
            dropdownClassName="mt-2"
            iconClassName="filter-none opacity-100"
            className="px-4 py-3 rounded-md bg-elr-black text-elr-white-400"
          />
        </div>
      </section>
      <section className="bg-elr-white-400 mb-8">
        <div className="py-4 md:px-8 px-4 flex justify-between items-center">
          <div className="flex flex-col">
            <h1 className="text-xl font-medium text-elr-black">
              {serviceProfile?.name}
            </h1>
            <p className="text-gray-500">
              Added{' '}
              {DateTime.fromJSDate(
                new Date(serviceProfile?.createdAt ?? '')
              ).toFormat('dd-MM-yyyy')}
            </p>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setIsSettingsOpen(true)}
              className="flex items-center gap-2 bg-elr-gray py-2 px-4 rounded-md"
            >
              <GearIcon fill="#081120" />
              Restaurant Settings
            </button>
            <button
              type="button"
              className="flex items-center gap-2 bg-elr-black text-white py-2 px-4 rounded-md"
              onClick={() => setIsSwitchRestaurantOpen(true)}
            >
              <SwitchIcon />
              Switch Restaurant
            </button>
          </div>
          <SwitchRestaurantModal
            isModalOpen={isSwitchRestaurantOpen}
            setIsModalOpen={setIsSwitchRestaurantOpen}
            serviceProfiles={serviceProfiles}
          />
        </div>
      </section>
      <DashboardCards data={cardData} />
      <section className="bg-elr-white-400 mb-8">
        <div className="py-4 md:px-8 px-2 md:flex justify-between border-b-2 border-elr-gray">
          <div className="flex self-center text-lg md:gap-16 md:justify-between justify-around border-elr-black-200 relative">
            {tabs.map((tab) => (
              <button
                key={tab.name}
                type="button"
                ref={tab.ref}
                className={getTabClassNames(activeTab === tab.name)}
                onClick={() => setActiveTab(tab.name)}
              >
                {tab.name}
              </button>
            ))}
            <div
              className={`rounded-t-lg h-1 bg-elr-black absolute transition-all duration-300 ease-in-out`}
              style={{
                width: `${underlinePosition.width}px`,
                left: `${underlinePosition.left}px`,
                bottom: '-24px',
              }}
            />
          </div>
          <div className="flex gap-4 md:flex-row flex-col">
            <div className="md:flex justify-between gap-4 bg-elr-gray px-4 py-2 rounded-sm w-72 hidden">
              <input
                type="text"
                placeholder="search order ID or item"
                className="outline-none w-full bg-elr-gray border-none focus:outline-0"
              />
              <SearchIcon />
            </div>
          </div>
        </div>

        {isUploadDashboard()}
      </section>
      <Settings
        isOpen={isSettingsOpen}
        setIsOpen={setIsSettingsOpen}
        outletType={ServiceOutletType.restaurant}
        outletTypeId={params.outletTypeId ?? ''}
        payload={{
          bankDetails: serviceProfile?.bankDetails,
          autoPayout: serviceProfile?.autoPayout,
        }}
      />
    </div>
  );
};

export default RestaurantDashboard;

interface TableRowProps {
  request: Request;
  index: number;
  activeTab: TabNames;
  toggleRow: (index: number) => void;
  isExpanded: number | null;
  isOrderReady: boolean;
}

export const TableRow: React.FC<TableRowProps> = ({
  request,
  index,
  toggleRow,
  isExpanded,
  isOrderReady,
  activeTab,
}) => {
  const { trackingId, priceEstimate, senderInformation, status } = request;

  const formatDateTime = (createdAt: string) => {
    const dateTime = DateTime.fromISO(createdAt);
    return dateTime.toFormat('yyyy-LL-dd HH:mm a');
  };

  const getButtonText = () => {
    if (isExpanded === index) {
      return 'Minimize';
    }
    return 'View Order';
  };

  return (
    <tr
      className="text-center border border-x-0 border-elr-gray-500 cursor-pointer"
      onClick={() => toggleRow(index)}
    >
      <td className="text-left py-5 pl-8 text-elr-black truncate ">
        <div className="truncate">{trackingId}</div>
        {activeTab !== TabNames.COMPLETED && (
          <div className="text-elr-black-300 text-sm pt-1">
            {formatDateTime(request.createdAt)}
          </div>
        )}
      </td>
      <td className="py-5 px-5 text-center text-elr-black text-opacity-60 truncate max-w-[150px] md:max-w-xs overflow-hidden">
        {truncate(senderInformation?.data?.name || 'N/A', { length: 20 })}
      </td>
      <td className="py-5 text-center text-elr-black text-opacity-60">
        {currencyFormatter(priceEstimate)}
      </td>
      {activeTab === TabNames.COMPLETED && (
        <td className="py-5 px-5 text-center text-elr-black text-opacity-60">
          {formatDateTime(request.createdAt)}
        </td>
      )}

      {activeTab === TabNames.ONGOING && (
        <td className="py-5 px-5 text-center text-elr-black w-60">
          {isOrderReady ? (
            <td className="text-center text-elr-black text-opacity-60">
              <p className={getRequestStatusClassNames(status)}>
                {status === RequestTypes.accepted
                  ? 'Awaiting Pickup'
                  : 'Awaiting Delivery'}
              </p>
            </td>
          ) : (
            <ElrButton
              text="Order is Ready"
              className="bg-elr-gray rounded-sm"
            />
          )}
        </td>
      )}

      {activeTab === TabNames.NEW_ORDERS && (
        <td className="py-5 px-5 text-center text-elr-black w-52">
          <button
            className="focus:outline-none flex justify-center gap-4 align-middle items-center px-6 py-3 w-full bg-elr-gray rounded-sm"
            type="button"
          >
            {getButtonText()}
            <img
              src={Dropdown}
              alt="Dropdown"
              className={getDropdownIconClassNames(isExpanded === index)}
            />
          </button>
        </td>
      )}
      {activeTab !== TabNames.NEW_ORDERS && (
        <td className="py-5 relative w-10">
          <button className="focus:outline-none" type="button">
            <img
              src={Dropdown}
              alt="Dropdown"
              className={getDropdownIconClassNames(isExpanded === index)}
            />
          </button>
        </td>
      )}
    </tr>
  );
};

interface ExpandedRowProps {
  request: Request;
  activeTab: TabNames;
  isOrderReady: boolean;
  onOrderReady: () => void;
}
export const ExpandedRow: React.FC<ExpandedRowProps> = ({
  request,
  activeTab,
  isOrderReady,
  onOrderReady,
}) => {
  const { deliverToInformation, designatedAgent } = request;
  const recipientInfo = getFirstItem(deliverToInformation);

  const riderName =
    designatedAgent?.firstname || designatedAgent?.lastname
      ? `${designatedAgent.firstname || ''} ${designatedAgent.lastname || ''}`
      : 'N/A';
  const riderPhone = designatedAgent?.phone || 'N/A';

  return (
    <tr className="text-left">
      <td colSpan={5} className="p-5 text-elr-black">
        <div className="flex flex-col gap-6">
          <div className="flex w-full p-8 bg-elr-gray md:gap-24 gap-12">
            <div className="flex flex-col w-1/4">
              <p>Recipient Details</p>
              <p className="text-elr-black text-opacity-60 py-1">
                {recipientInfo?.address.fullAddress || 'N/A'}
              </p>
              <p className="text-elr-black text-opacity-60">
                {recipientInfo?.phone || 'N/A'}
              </p>
            </div>

            <div className="flex flex-col w-1/3">
              <p>Order Details</p>
            </div>
            {activeTab === TabNames.NEW_ORDERS && (
              <div className="flex flex-col gap-4">
                <ElrButton
                  text="Decline Order"
                  onClick={() => {}}
                  size="sm"
                  className="bg-white text-elr-error rounded-md px-4 py-2"
                />
                <ElrButton
                  text="Accept Order"
                  onClick={() => {}}
                  size="sm"
                  className="bg-elr-black text-white rounded-md px-4 py-2"
                />
              </div>
            )}

            {activeTab === TabNames.ONGOING && (
              <div>
                {!isOrderReady ? (
                  <div className="flex flex-col flex-1">
                    <p className="text-elr-black text-opacity-60 py-1 w-9/12">
                      Rider details will appear once you confirm the order is
                      ready.
                    </p>
                    <div className="pt-10">
                      <ElrButton
                        text="Order is Ready"
                        size="sm"
                        onClick={onOrderReady}
                        className="bg-elr-black text-white rounded-md px-4 py-2"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col flex-1">
                    <p>Rider Details</p>
                    <p className="text-elr-black text-opacity-60 py-1">
                      {riderName}
                    </p>
                    <p className="text-elr-black text-opacity-60 py-1">
                      {riderPhone}
                    </p>
                  </div>
                )}
              </div>
            )}

            {activeTab === TabNames.COMPLETED && (
              <div className="flex flex-col">
                <p>Rider Details</p>
                <p className="text-elr-black text-opacity-60 py-1">
                  {riderName}
                </p>
                <p className="text-elr-black text-opacity-60 py-1">
                  {riderPhone}
                </p>
              </div>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
};

const SwitchRestaurantModal = ({
  isModalOpen,
  setIsModalOpen,
  serviceProfiles = [],
}: {
  isModalOpen: boolean;
  setIsModalOpen: (value: boolean) => void;
  serviceProfiles: ServiceProfile[];
}) => {
  const [selectedRestaurant, setSelectedRestaurant] = useState<string | null>(
    null
  );
  const navigate = useNavigate();

  const handleViewRestaurant = () => {
    if (selectedRestaurant) {
      navigate(
        `/${COMMERCE}/${privateRoutes.ecommerce}/${privateRoutes.restaurant}/${selectedRestaurant}/${privateRoutes.restaurantDashboard}`
      );
      setIsModalOpen(false);
    }
  };

  return (
    <ElrModal
      isOpen={isModalOpen}
      onClose={() => setIsModalOpen(false)}
      height="h-[543px]"
      width="w-[568px]"
    >
      <div className="w-full h-full flex flex-col">
        <h3 className="text-2xl text-center my-6">Switch Restaurant</h3>
        <div className="flex-1 overflow-y-auto px-1">
          {serviceProfiles.length ? (
            serviceProfiles.map((restaurant) => (
              <button
                key={restaurant._id}
                type="button"
                className={`flex items-center justify-between w-full py-4 px-2 cursor-pointer ${
                  selectedRestaurant === restaurant._id
                    ? 'border-elr-black bg-elr-gray-50'
                    : 'border-elr-gray-100'
                }`}
                onClick={() => setSelectedRestaurant(restaurant._id)}
              >
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-[#f7f7f7] flex items-center justify-center">
                    <CutleryIconWonWithBG
                      fill="#8E8E93"
                      width="46"
                      height="46"
                      bgFill="fff"
                    />
                  </div>
                  <div className="flex flex-col items-start gap-1">
                    <h4 className="text-lg font-medium">{restaurant.name}</h4>
                    <p className="text-base text-elr-black-300">
                      Added{' '}
                      {DateTime.fromJSDate(
                        new Date(restaurant.createdAt)
                      ).toFormat('dd-MM-yyyy')}
                    </p>
                  </div>
                </div>
                <ElrRadio
                  checked={selectedRestaurant === restaurant._id}
                  radioClassName="accent-elr-black w-5 h-5"
                  onChange={() => setSelectedRestaurant(restaurant._id)}
                />
              </button>
            ))
          ) : (
            <p className="text-center text-elr-black-300">
              No restaurants available.
            </p>
          )}
        </div>
        <div className="py-12">
          <ElrButton
            text="View Restaurant"
            className="w-full py-2 bg-elr-black text-white rounded-md"
            onClick={handleViewRestaurant}
            disabled={!selectedRestaurant}
          />
        </div>
      </div>
    </ElrModal>
  );
};
